import { useState, useCallback } from 'react'
import { api } from '../api/client.js'

export type SmsGatewayProvider = 'pldt' | 'mobile360' | 'twilio' | 'yeastar'

export interface SmsGatewayResponse {
  id: string
  status: boolean
  isDefault: boolean
  name: string
  description: string | null
  campaignId: string | null
  provider: SmsGatewayProvider
  providerConfig: Record<string, unknown>
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
  updatedAt: string
  createdByName: string | null
  updatedByName: string | null
}

export interface SmsGatewayCreateInput {
  status?: boolean
  isDefault?: boolean
  name: string
  description?: string | null
  campaignId?: string | null
  provider: SmsGatewayProvider
  providerConfig?: Record<string, unknown>
}

export interface SmsGatewayUpdateInput {
  status?: boolean
  isDefault?: boolean
  name?: string
  description?: string | null
  campaignId?: string | null
  provider?: SmsGatewayProvider
  providerConfig?: Record<string, unknown>
}

export interface SmsGatewaySummary {
  total: number
  active: number
  inactive: number
  default: number
}

export interface SmsGatewayListResponse {
  items: SmsGatewayResponse[]
  total: number
  limit: number
  offset: number
  summary: SmsGatewaySummary
}

export function useSmsGateways() {
  const [items, setItems] = useState<SmsGatewayResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState<{ total: number; limit: number; offset: number; totalPages: number } | null>(null)
  const [summary, setSummary] = useState<SmsGatewaySummary | null>(null)

  const fetchItems = useCallback(async (searchQuery = '', page = 1, limit = 20) => {
    setLoading(true)
    try {
      const offset = (page - 1) * limit
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''
      const response = await api.get<SmsGatewayListResponse>(`/admin/channels/sms-gateways?limit=${limit}&offset=${offset}${searchParam}`)
      setItems(response.items)
      setPagination({
        total: response.total,
        limit: response.limit,
        offset: response.offset,
        totalPages: Math.ceil(response.total / response.limit)
      })
      setSummary(response.summary ?? null)
    } catch (error) {
      console.error('Error fetching SMS gateways:', error)
      setItems([])
      setPagination(null)
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const create = useCallback(async (data: SmsGatewayCreateInput) => {
    setLoading(true)
    try {
      const response = await api.post<SmsGatewayResponse>('/admin/channels/sms-gateways', data)
      return response
    } catch (error) {
      console.error('Error creating SMS gateway:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const update = useCallback(async (id: string, data: SmsGatewayUpdateInput) => {
    setLoading(true)
    try {
      const response = await api.put<SmsGatewayResponse>(`/admin/channels/sms-gateways/${id}`, data)
      return response
    } catch (error) {
      console.error('Error updating SMS gateway:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const remove = useCallback(async (id: string) => {
    setLoading(true)
    try {
      const response = await api.delete<{ success: boolean }>(`/admin/channels/sms-gateways/${id}`)
      return response
    } catch (error) {
      console.error('Error deleting SMS gateway:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  const getById = useCallback(async (id: string) => {
    try {
      const response = await api.get<{ item: SmsGatewayResponse }>(`/admin/channels/sms-gateways/${id}`)
      return response
    } catch (error) {
      console.error('Error fetching SMS gateway:', error)
      throw error
    }
  }, [])

  return {
    items,
    loading,
    pagination,
    summary,
    refetch: fetchItems,
    create,
    update,
    remove,
    getById,
  }
}
